<script setup lang="ts">
/**
 * Global settings' Workers (shared/workers.ts): other machines running the
 * bridge, which take a Ready queued Claude when this box has no room and hand
 * its worktree back once it is idle. A worker is made here by name and gets a
 * token, shown once with the env file it goes in (deploy/README.md: A worker
 * machine). Paused, it is sent nothing new; revoked, its token stops working.
 * A worktree out on a worker that is gone for good is given up here: forgotten,
 * and its task put back in todo.
 */
import { ref } from "vue";
import { api } from "../lib/api.js";
import { usePoll } from "../lib/poll.js";
import { copyText } from "../lib/clipboard.js";
import { toast } from "../lib/toast.js";
import type { WorkerInfo } from "@shared/workers.js";

/** While the card is open the list is read again this often: connected, busy and room move. */
const REFRESH_MS = 10_000;

const workers = ref<WorkerInfo[] | null>(null);
const error = ref("");
const busy = ref(false);
const name = ref("");
/** The token just made, shown until dismissed: never again after. */
const made = ref<{ name: string; env: string } | null>(null);
/** Two clicks for what cannot be undone: `<worker id>` to revoke, `<id>/<repo>/<worktree>` to give up. */
const confirming = ref<string | null>(null);

const message = (err: unknown): string => (err instanceof Error ? err.message : String(err));

async function load(): Promise<boolean> {
  try {
    workers.value = (await api.get<{ workers: WorkerInfo[] }>("/api/workers")).workers;
    error.value = "";
    return true;
  } catch (err) {
    error.value = message(err);
    return false;
  }
}

async function act(work: () => Promise<{ workers: WorkerInfo[] }>): Promise<void> {
  busy.value = true;
  error.value = "";
  try {
    workers.value = (await work()).workers;
  } catch (err) {
    error.value = message(err);
  } finally {
    busy.value = false;
    confirming.value = null;
  }
}

async function create(): Promise<void> {
  const wanted = name.value.trim();
  if (!wanted || busy.value) return;
  busy.value = true;
  error.value = "";
  try {
    const got = await api.post<{ worker: { name: string }; env: string }>("/api/workers", { name: wanted });
    made.value = { name: got.worker.name, env: got.env };
    name.value = "";
    await load();
  } catch (err) {
    error.value = message(err);
  } finally {
    busy.value = false;
  }
}

const setPaused = (w: WorkerInfo, paused: boolean): Promise<void> => act(() => api.put(`/api/workers/${w.id}/paused`, { paused }));

function revoke(w: WorkerInfo): void {
  if (confirming.value !== w.id) {
    confirming.value = w.id;
    return;
  }
  void act(() => api.del(`/api/workers/${w.id}${w.worktrees.length ? "?force=1" : ""}`));
}

function giveUp(w: WorkerInfo, t: WorkerInfo["worktrees"][number]): void {
  const key = `${w.id}/${t.repo}/${t.worktree}`;
  if (confirming.value !== key) {
    confirming.value = key;
    return;
  }
  void act(() => api.post(`/api/workers/${w.id}/forget`, { repo: t.repo, worktree: t.worktree }));
}

async function copyEnv(): Promise<void> {
  if (!made.value) return;
  await copyText(made.value.env);
  toast("Copied the worker's env file");
}

const when = (ms: number | null): string => (ms === null ? "never" : new Date(ms).toLocaleString());

/** What a connected worker has room for, in a line. */
function roomLine(w: WorkerInfo): string {
  const r = w.room;
  if (!r) return `${w.busy} at work`;
  const parts = [`${w.busy} of ${r.max} at work`, `${r.freeMb} MB free`];
  if (r.cpu !== null) parts.push(`CPU ${Math.round(r.cpu)}%`);
  return parts.join(" · ");
}

usePoll(load, REFRESH_MS);
</script>

<template>
  <div class="money-card profile-card">
    <h3>Workers</h3>
    <p class="money-hint">
      Other machines that run Claudes for this board. When this box has no room under Parallel Claudes, a
      queued task set to Ready goes to a connected worker that has room instead. It runs in tmux there (its
      session shows in the tmux view, named after the worker). Once its Claude has been idle for a few minutes
      with nobody attached, the worktree comes back here as one of this box's. Setting one up is in
      deploy/README.md, under “A worker machine”.
    </p>

    <form class="integration-row" @submit.prevent="create">
      <input v-model="name" maxlength="16" placeholder="name — e.g. laptop" aria-label="Worker name" autocomplete="off" :disabled="busy" />
      <button class="btn primary" type="submit" :disabled="!name.trim() || busy">Add worker</button>
    </form>
    <p v-if="error" class="integration-error">{{ error }}</p>

    <div v-if="made" class="feedback-key">
      <div class="integration-row">
        <strong>{{ made.name }}’s env file</strong>
        <span class="integration-muted">its token is shown only now</span>
        <span style="flex: 1"></span>
        <button class="btn" type="button" @click="copyEnv">Copy</button>
        <button class="btn ghost" type="button" @click="made = null">Done</button>
      </div>
      <pre class="integration-code primer-response">{{ made.env }}</pre>
    </div>

    <p v-if="workers && !workers.length" class="integration-muted">No workers yet.</p>
    <div v-for="w in workers ?? []" :key="w.id" class="feedback-key">
      <div class="integration-row feedback-key-head">
        <strong>{{ w.name }}</strong>
        <span v-if="w.connected" class="integration-muted">
          connected<template v-if="w.lastAddress"> from {{ w.lastAddress }}</template> · {{ roomLine(w) }}<template v-if="w.paused"> · paused</template>
        </span>
        <span v-else class="integration-muted">offline · last seen {{ when(w.lastSeenAt) }}<template v-if="w.paused"> · paused</template></span>
        <span style="flex: 1"></span>
        <button class="btn ghost" type="button" :disabled="busy" @click="setPaused(w, !w.paused)">{{ w.paused ? "Resume" : "Pause" }}</button>
        <button
          class="btn ghost"
          :class="{ danger: confirming === w.id }"
          type="button"
          :disabled="busy"
          :title="w.worktrees.length ? 'Its worktrees out are given up with it' : ''"
          @click="revoke(w)"
          @blur="confirming = null"
        >{{ confirming === w.id ? "Really revoke?" : "Revoke" }}</button>
      </div>
      <p v-if="w.refused" class="integration-error">Refused: it {{ w.refused }}.</p>
      <div v-for="t in w.worktrees" :key="`${t.repo}/${t.worktree}`" class="integration-row">
        <span class="feedback-way">⎇ {{ t.worktree }}</span>
        <span class="integration-muted">{{ t.repo }}<template v-if="t.title"> · {{ t.title }}</template><template v-if="t.landed"> · home, being tidied up</template></span>
        <span style="flex: 1"></span>
        <button
          class="btn ghost"
          :class="{ danger: confirming === `${w.id}/${t.repo}/${t.worktree}` }"
          type="button"
          :disabled="busy"
          title="Forget it here and put its task back in todo — for a worker that is not coming back"
          @click="giveUp(w, t)"
          @blur="confirming = null"
        >{{ confirming === `${w.id}/${t.repo}/${t.worktree}` ? "Really give up?" : "Give up" }}</button>
      </div>
    </div>
  </div>
</template>
